/**
 * Impression d'un rapport, et donc enregistrement en PDF, par la boîte
 * d'impression du navigateur.
 *
 * Pas de bibliothèque PDF : le texte reste sélectionnable et les polices
 * nettes, ce qu'une capture en image perdrait.
 *
 * Une séance d'impression :
 *  - pose une classe sur <body>, qui active les règles `@media print` du
 *    rapport et d'aucun autre écran ;
 *  - écarte les règles `@page` des autres écrans le temps d'imprimer. Aucun
 *    sélecteur ne peut restreindre une règle `@page` : celles du certificat
 *    (`A4 portrait; margin: 0`) s'appliqueraient sinon au rapport ;
 *  - pose la nôtre, en fin de <head> ;
 *  - change le titre du document, que le navigateur propose comme nom du
 *    fichier PDF.
 *
 * `start` peut être appelée deux fois (clic, puis `beforeprint`) : rien n'est
 * perdu, `stop` remet tout en place une seule fois.
 */
export function createPrintSession({
  bodyClass,
  styleId,
  pageRule = "@page { size: A4 portrait; margin: 10mm; }",
}) {
  let silenced = [];
  let savedTitle = null;

  function silenceOtherPageRules() {
    const collect = (container) => {
      // Parcours à l'envers : supprimer décale les index suivants.
      for (let index = container.cssRules.length - 1; index >= 0; index -= 1) {
        const rule = container.cssRules[index];
        if (rule.type === CSSRule.PAGE_RULE) {
          silenced.push({ container, index, cssText: rule.cssText });
          container.deleteRule(index);
        } else if (rule.cssRules) {
          collect(rule);
        }
      }
    };

    Array.from(document.styleSheets).forEach((sheet) => {
      const node = sheet.ownerNode;
      if (node && node.id === styleId) return;
      try {
        collect(sheet);
      } catch (error) {
        // Feuille d'une autre origine : illisible, et sans `@page` à nous.
      }
    });
  }

  function restoreOtherPageRules() {
    silenced
      .slice()
      .reverse()
      .forEach(({ container, index, cssText }) => {
        try {
          container.insertRule(cssText, index);
        } catch (error) {
          console.error("Restauration d'une règle @page :", error);
        }
      });
    silenced = [];
  }

  function applyPageStyle() {
    let node = document.getElementById(styleId);
    if (!node) {
      node = document.createElement("style");
      node.id = styleId;
    }
    node.textContent = pageRule;
    // appendChild sur un nœud déjà présent le déplace en dernier.
    document.head.appendChild(node);
  }

  function removePageStyle() {
    const node = document.getElementById(styleId);
    if (node && node.parentNode) node.parentNode.removeChild(node);
  }

  return {
    start({ title } = {}) {
      document.body.classList.add(bodyClass);
      silenceOtherPageRules();
      applyPageStyle();
      if (title) {
        if (savedTitle === null) savedTitle = document.title;
        document.title = title;
      }
    },
    stop() {
      document.body.classList.remove(bodyClass);
      removePageStyle();
      restoreOtherPageRules();
      if (savedTitle !== null) {
        document.title = savedTitle;
        savedTitle = null;
      }
    },
  };
}
